import { useContext, useLayoutEffect } from 'react';
import { useLatest } from 'react-use';
import { PanelSection } from '../../../SidePanel';
import useAnchorData from '../../../../hooks/useAnchorData';
import { useNotesContext } from '../../../../contexts/NotesContext';
import NoteEditor from './NoteEditor';
import useReadonly from '../../../../hooks/useReadonly';
import { MapContext } from '../../../../contexts/MapContext';
import { NOTE_ANCHOR_TYPES } from '../../../../constants/notes';
import { mapModes } from '../../../../constants/map';
import { geojsonFeatureIdFromRecordIds } from '../../../../utils/layers';

const { LAYER, LAYER_FEATURE, SECTION, FARM } = NOTE_ANCHOR_TYPES;

const NotesSection = ({
  // Component that displays list of Notes.
  NotesList,
  // Anchor ID to use for new notes. (Null for non-anchor views like Farm)
  anchorId = null,
  // Optional header Component, may have title & "New Note +" button.
  Header = () => null,
}) => {
  const readonly = useReadonly();

  const {
    mapState: { setMapMode },
    layers: { allLayerFeatureRecords },
  } = useContext(MapContext);

  const {
    loading,
    notesById,
    mutationLoading,
    currentNoteId,
    setCurrentNoteId,
    saveNote,
    deleteNote,
  } = useNotesContext();

  const currentNote = notesById[currentNoteId];

  // Look up anchorType and anchorName (if anchorId is not provided, use currentNoteId to determine anchorId)
  const { anchorType, anchorName } = useAnchorData(
    currentNote?.anchorId ?? anchorId,
  );

  const getGeojsonFeatureId = (layerFeatureId) => {
    const { layerId } = allLayerFeatureRecords[layerFeatureId] ?? {};
    if (!layerId) throw new Error("Couldn't find layerId for layer feature.");

    return geojsonFeatureIdFromRecordIds(layerId, layerFeatureId);
  };

  // Switch to the appropriate map mode when anchor name is clicked (i.e. show the anchor)
  const onAnchorNameClick = () => {
    const id = currentNote?.anchorId ?? anchorId;

    if (anchorType === LAYER) {
      setMapMode(mapModes.LAYER_ENGAGED, id);
    } else if (anchorType === LAYER_FEATURE) {
      const geojsonFeatureId = getGeojsonFeatureId(id);
      setMapMode(mapModes.LAYER_FEATURE_ENGAGED, geojsonFeatureId);
    } else if (anchorType === SECTION) {
      setMapMode(mapModes.SECTION_ENGAGED, id);
    } else if (anchorType === FARM) {
      setMapMode(mapModes.VIEW);
    } else {
      throw new Error('Unknown anchor type.');
    }
  };

  const currentNoteRef = useLatest(currentNote);

  /**
   * This closes NoteEditor when anchorId changes.
   *
   * One exception is when the anchorId matches the note -- i.e. when we're
   * switching from farm view to view the note's anchor.
   *
   * NOTE: we use `useLayoutEffect` here to avoid a "flickering" effect where:
   *  1. this component mounts and renders w/ the NoteEditor open
   *  2. this hook runs (if it were a `useEffect`) and resets currentNoteId
   *  3. that causes the NoteEditor to close
   *
   * By using `useLayoutEffect`, the NoteEditor gets closed before the user sees
   * it.
   */
  useLayoutEffect(() => {
    const currentNoteAnchorId = currentNoteRef.current?.anchorId ?? null; // null to match the default value of `anchorId`

    if (currentNoteAnchorId !== anchorId) {
      setCurrentNoteId(null);
    }
  }, [
    anchorId, // (the actual thing we want triggering this effect)
    setCurrentNoteId, // stable, but eslint doesn't know
    currentNoteRef, // stable, but eslint doesn't know
  ]);

  return (
    <PanelSection className="px-0">
      <Header />

      <NotesList />

      {currentNoteId !== null && (
        <NoteEditor
          note={currentNote ?? { anchorType, anchorId }}
          anchorName={anchorName}
          onAnchorNameClick={onAnchorNameClick}
          readonly={readonly || mutationLoading}
          close={() => {
            if (!loading) setCurrentNoteId(null);
          }}
          saveNote={saveNote}
          deleteNote={deleteNote}
        />
      )}
    </PanelSection>
  );
};

export default NotesSection;
